import { useAuthentication } from "@src/auth"
import { TrackerSelector } from "@src/components/TrackerSelector"
import { config } from "@src/constants"
import { usePartner } from "@src/partner"
import { useUser } from "@src/user"
import React from "react"
import { Button, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"

const styles = {
  serialNumberInput: {
    marginTop: "15px",
  },
}

export const ManageTrackers: React.FC = () => {
  const { t } = useTranslation()
  const { userEmail } = useAuthentication()
  const { partner } = usePartner()
  const { user } = useUser()
  const [serialNumbers, setSerialNumbers] = React.useState("")
  const [action, setAction] = React.useState<"remove" | "transfer">("remove")
  const [fleetName, setFleetName] = React.useState("")
  const [selectedDetails, setSelectedDetails] = React.useState<
    { serialnumber: string; name: string }[]
  >([])

  const selectedSerials = React.useMemo(
    () =>
      Array.from(
        new Set(
          serialNumbers
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ),
    [serialNumbers],
  )

  const handleSelectorChange = (serials: string[]) => {
    setSerialNumbers(serials.join("; "))
  }

  const createEmailLink = () => {
    const subject = `Request for ${action === "remove" ? "Tracker Removal" : "Tracker Transfer"} from My Account`

    const baseBody = `Dear Customer Care Team,

I would like to request ${action === "remove" ? "the removal of trackers from my account" : `to transfer trackers to "${fleetName}"`}.

My account details:
- Email: ${userEmail}
- Name: ${user?.firstname} ${user?.lastname}
- Address: ${user?.address}
- Partner: ${partner?.name} (${partner?.uuid})

Trackers ${action === "remove" ? "to be removed" : "to be transferred"}:
- Serial Numbers: ${serialNumbers || "[Please fill in serial numbers]"}


Could you please ${action === "remove" ? "remove these trackers from my account" : `transfer these trackers to the "${fleetName}" fleet`}?

Thank you for your assistance.

Best regards,
${user?.firstname} ${user?.lastname}`

    const body = baseBody.replace(
      "[SERIAL_NUMBERS]",
      serialNumbers || "[Please fill in serial numbers]",
    )

    return `mailto:${config.customerCareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <div>
      <div>
        <h5>{t("settings.partner.manage_trackers_title")}</h5>
        <p className="mb-3">{t("settings.partner.manage_trackers_description")}</p>

        <div className="mb-2">
          <label className="form-label">{t("settings.partner.action_label")}:</label>
          <div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="action"
                id="remove"
                checked={action === "remove"}
                onChange={() => setAction("remove")}
              />
              <label className="form-check-label" htmlFor="remove">
                {t("settings.partner.remove")}
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="action"
                id="transfer"
                checked={action === "transfer"}
                onChange={() => setAction("transfer")}
              />
              <label className="form-check-label" htmlFor="transfer">
                {t("settings.partner.transfer")}
              </label>
            </div>
          </div>
        </div>

        {action === "transfer" && (
          <div className="mb-3">
            <label className="form-label">{t("settings.partner.fleet_name")}:</label>
            <Form.Control
              type="text"
              value={fleetName}
              onChange={(e) => setFleetName(e.target.value)}
              placeholder={t("settings.partner.fleet_name_placeholder")}
            />
          </div>
        )}

        <div className="mb-2" style={styles.serialNumberInput}>
          <label className="form-label">{t("settings.partner.serial_numbers_label")}:</label>
          <div className="mb-2 d-flex flex-wrap gap-2">
            {selectedDetails.map((d) => (
              <span key={d.serialnumber} className="badge bg-secondary">
                {d.serialnumber} - {d.name}
                <span
                  role="button"
                  aria-label="remove"
                  className="ms-2"
                  onClick={() =>
                    handleSelectorChange(selectedSerials.filter((s) => s !== d.serialnumber))
                  }
                >
                  ×
                </span>
              </span>
            ))}
          </div>
          <div className="mt-2">
            <TrackerSelector
              selectedSerials={selectedSerials}
              onChange={handleSelectorChange}
              onChangeDetails={setSelectedDetails}
              allowFilter={false}
            />
          </div>
        </div>

        <Button
          variant="primary"
          href={createEmailLink()}
          disabled={selectedSerials.length === 0 || (action === "transfer" && !fleetName.trim())}
        >
          {t("settings.partner.send_email_button")}
        </Button>
      </div>
    </div>
  )
}
